import { newGuid } from "@bps/common-utils";
import { Country } from "@libs/enums/country.enum.ts";
import {
  AddOrganisationContactDto,
  AddressType,
  CommunicationDto,
  CommunicationType,
  ContactStatus,
  ContactType
} from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { RootStore } from "@stores/root/RootStore.ts";

import { Labels } from "../types/labels.enums.types.ts";
import { toAddressDto } from "../utils/contact.utils.ts";
import { QuickAddContactFormValues } from "./QuickAddContactFormValues.tsx";

export const addContactByType = async (
  values: QuickAddContactFormValues,
  type: ContactType,
  root: RootStore
) => {
  const { mobile, firstName, lastName, email, addresses, hasPostal } = values;

  const { practice, core } = root;

  const communications: CommunicationDto[] = [];

  if (mobile) {
    communications.push({
      type: CommunicationType.Mobile,
      preferred: true,
      value: mobile
    });
  }

  if (email) {
    communications.push({
      type: CommunicationType.Email,
      value: email
    });
  }

  let filteredAddress = addresses;

  if (!hasPostal && addresses) {
    filteredAddress = addresses?.filter(a => a.type !== AddressType.Postal);
  }

  const data = {
    fullName: { firstName, lastName },
    addresses: toAddressDto(filteredAddress),
    communications
  };

  switch (type) {
    case ContactType.Patient:
      return await practice.addPatient(data);
    case ContactType.Individual:
      return await practice.addIndividual({
        ...data,
        isHealthProvider: false
      });
    case ContactType.Organisation:
      const orgData: AddOrganisationContactDto = {
        organisationName: values.organisationName,
        organisationCategories: values.organisationCategories,
        communications: values.phone
          ? [
              {
                type: CommunicationType.WorkPhone,
                preferred: true,
                value: values.phone
              }
            ]
          : [],
        contactStatus: ContactStatus.Active,
        draftItemsEnabled: false,
        isHealthProvider: false
      };

      if (values.suburb || values.state || values.city || values.postCode) {
        orgData.addresses = [
          {
            type: AddressType.Physical,
            suburb: values.suburb,
            state: values.state,
            postCode: values.postCode,
            city: values.city,
            country: core.tenantDetails?.country
          }
        ];
      }
      return await practice.addOrganisation(orgData);
  }
};

export const getQuickAddInitialValues = (
  country: Country | undefined,
  type: ContactType,
  options: {
    defaultValues: QuickAddContactFormValues | undefined;
    detailedView?: boolean;
  }
) => {
  const { detailedView, defaultValues } = options;
  if (detailedView && type === ContactType.Individual && country) {
    return {
      ...defaultValues,
      addresses: [
        {
          street1: undefined,
          country,
          type: AddressType.Physical,
          isNew: true,
          id: newGuid()
        }
      ]
    };
  }
  return defaultValues;
};

export const getQuickAddTitle = (
  title: string | undefined,
  type: ContactType,
  patientLabel: string
) => {
  if (title) {
    return title;
  } else {
    switch (type) {
      case ContactType.Individual:
        return Labels.newIndividual;
      case ContactType.Patient:
        return `New ${patientLabel}`;
      case ContactType.Organisation:
        return Labels.newOrganisation;
    }
  }
};
